<template>
  <v-dialog
      v-model="show"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      @keydown.esc="close"
  >
    <v-card class="live-editor-wrapper" tile>
      <!-- HEADER -->

      <!-- BODY -->
      <div class="editor-body">
        <!-- LEFT: CANVAS AREA -->
        <div class="canvas-container" ref="canvasRef">
          <v-toolbar color="primary" dark dense elevation="0">
            <v-btn icon dark @click="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title>
              Live Editor - {{ orderName }}
              <span class="text-caption ml-2" style="opacity: 0.7">
                ({{ configMaterial }} / {{ effectiveOrientation }} / {{ configSize }})
              </span>
            </v-toolbar-title>
          </v-toolbar>
          <div class="canvas-inner">
            <div
                @click="() => (dialogImage = currentImageUrl)"
                class="zoom-hint d-flex align-center"
            >
              <v-icon small>mdi-alpha-y-box-outline</v-icon>
              =
              <v-icon small>mdi-loupe</v-icon>
            </div>
            <!-- FRAME -->
            <div
                ref="frameRef"
                class="print-frame elevation-10"
                :class="{ 'print-frame--drag-over': isFrameDragOver }"
                :style="frameContainerStyle"
                @mousedown.prevent="startArtworkDrag"
                @wheel.prevent="onWheel"
                @dragover.prevent="onFrameDragOver"
                @dragenter.prevent="onFrameDragEnter"
                @dragleave.prevent="onFrameDragLeave"
                @drop.prevent="onFrameDrop"
            >
              <!-- Artwork -->
              <img
                  :key="task.updateKey"
                  v-if="currentImageUrl"
                  :src="currentImageUrl"
                  class="artwork-layer"
                  :style="artworkImageStyle"
                  @load="onImageLoad"
                  draggable="false"
              />
              <div v-else class="loading-placeholder">
                <v-progress-circular
                    indeterminate
                    color="grey"
                ></v-progress-circular>
              </div>

              <!-- Draggable Texts -->
              <div
                  v-for="(element, idx) in localTextElements"
                  :key="'text-' + idx"
                  class="text-layer"
                  :style="getTextStyle(element, idx)"
                  @mousedown.stop.prevent="startTextDrag($event, idx)"
              >
                {{ element.text }}
              </div>

              <!-- Grid -->
              <div class="grid-lines"></div>
            </div>

            <!-- Floating Controls (unter dem Frame) -->
            <div class="floating-controls elevation-4">
              <div class="d-flex align-center pa-2">
                <v-icon small class="mr-2">mdi-magnify-minus</v-icon>
                <v-slider
                    v-model="zoomProxy"
                    :min="zoomMin"
                    :max="zoomMax"
                    step="0.01"
                    hide-details
                    dense
                    class="mx-2"
                    @input="onSliderZoom"
                ></v-slider>
                <v-icon small class="ml-2">mdi-magnify-plus</v-icon>
                <span class="ml-3 caption font-weight-bold" style="width: 40px">
                  {{ (localCanvas.scale * 100).toFixed(0) }}%
                </span>
              </div>

              <v-divider class="my-1"></v-divider>

              <div class="d-flex justify-space-between pa-1">
                <v-btn-toggle
                    v-model="selectedVariant"
                    mandatory
                    dense
                    rounded
                    x-small
                >
                  <v-btn value="ORIGINAL">Original</v-btn>
                  <v-btn
                      v-for="(url, key) in validUpscales"
                      :key="key"
                      :value="key"
                  >
                    {{ mapVariantName(key) }}
                  </v-btn>
                  <v-btn
                      :disabled="!localRefinementArtwork && !effectiveRefinementArtwork"
                      v-if="effectiveRefinementArtwork || isRefinementView"
                      value="REFINEMENT"
                  >
                    Refinement
                  </v-btn>
                </v-btn-toggle>
                <v-btn @click="downloadArtwork" icon>
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: SIDEBAR -->
        <div class="settings-sidebar">
          <div class="pa-4 sidebar-inner">
            <!-- PREVIEWS -->
            <div class="sidebar-inner-settings">
              <div
                  v-if="task.revisionComment"
                  class="preview-box preview-box--warning mb-2"
              >
                <div class="text-caption grey--text mb-1">
                  Revision Comment / {{ task.revisionComment.userName }} /
                  {{ getDateFromNow(task.revisionComment.timestamp) }}
                </div>
                <span>{{ task.revisionComment.comment || task.revisionComment }}</span>
              </div>
              <div
                  v-if="customizationData.originalImage && currentImageUrl"
                  class="mt-2 mb-2"
              >
                <v-btn color="success" small block @click="openCompareDialog">
                  <v-icon left>mdi-compare</v-icon>
                  Compare Artwork/Original
                </v-btn>
              </div>
              <div class="preview-box mb-2">
                <div class="text-caption grey--text mb-1">Customer Preview</div>
                <v-btn
                    @click="
                  downloadFileFromUrl(
                    customizationData.artworkImageUrl,
                    `${task.orderName}_artwork`
                  )
                "
                    class="preview-box--download-btn"
                    icon
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
                <img
                    @click="() => (dialogImage = customizationData.previewImg)"
                    :src="customizationData.previewImg"
                    class="preview-img preview-img-main"
                    :key="task.updateKey"
                />
              </div>
              <div
                  v-if="customizationData.originalImage"
                  class="preview-box preview-box-small mb-4"
              >
                <div class="text-caption grey--text mb-1">Original Upload</div>
                <v-btn
                    @click="
                  downloadFileFromUrl(
                    customizationData.originalImage,
                    `${task.orderName}_original`
                  )
                "
                    class="preview-box--download-btn"
                    icon
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
                <img
                    @click="() => (dialogImage = customizationData.originalImage)"
                    :src="customizationData.originalImage"
                    :key="task.updateKey"
                    class="preview-img preview-img-small"
                />
              </div>

              <v-divider class="mb-4"></v-divider>

              <!-- TEXT SETTINGS -->
              <div class="d-flex justify-space-between align-center mb-2">
                <div class="text-subtitle-1 font-weight-bold">Texts</div>
                <div>
                  <v-btn icon small @click="deleteTexts">
                    <v-icon>mdi-delete-forever</v-icon>
                  </v-btn>
                  <v-btn icon small @click="resetTexts">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </div>
              </div>

              <v-expansion-panels accordion multiple v-model="openPanels" flat>
                <v-expansion-panel
                    v-for="(element, index) in localTextElements"
                    :key="index"
                    class="grey lighten-5 mb-2 rounded border-panel"
                >
                  <v-expansion-panel-header class="py-2">
                    Text #{{ index + 1 }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-text-field
                        v-model="element.text"
                        label="Content"
                        outlined
                        dense
                        hide-details
                        class="mb-3 white"
                    ></v-text-field>

                    <v-row dense>
                      <v-col cols="8">
                        <v-select
                            v-model="element.font"
                            :items="fontOptions"
                            label="Font"
                            outlined
                            dense
                            hide-details
                            class="white"
                            :menu-props="{ bottom: true, offsetY: true }"
                        >
                          <template v-slot:item="{ item }">
                            <span :style="{ fontFamily: item }">{{ item }}</span>
                          </template>
                        </v-select>
                      </v-col>
                      <v-col cols="4">
                        <div
                            class="color-swatch"
                            :style="{ backgroundColor: element.color }"
                            @click="openColorPicker(index)"
                        >
                          <input
                              type="color"
                              v-model="element.color"
                              :ref="'cp' + index"
                              class="hidden-input"
                          />
                        </div>
                      </v-col>
                    </v-row>

                    <div class="mt-4">
                      <div class="caption grey--text">
                        Size ({{ element.size }}%)
                      </div>
                      <v-slider
                          v-model="element.size"
                          min="1"
                          max="20"
                          step="0.1"
                          dense
                          hide-details
                      ></v-slider>
                    </div>

                    <div class="mt-2">
                      <div class="caption grey--text">
                        Pos Y ({{ element.y }}%)
                      </div>
                      <v-slider
                          v-model="element.y"
                          min="0"
                          max="100"
                          step="0.5"
                          dense
                          hide-details
                      ></v-slider>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-divider class="mt-4 mb-3"></v-divider>
              <!-- ARTWORK SETTINGS -->
              <div class="d-flex justify-space-between align-center mb-2">
                <div class="text-subtitle-1 font-weight-bold">Artwork</div>
                <v-btn icon small @click="resetArtworkTransform">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </div>

              <v-expansion-panels
                  accordion
                  multiple
                  flat
                  v-model="openArtworkPanels"
              >
                <v-expansion-panel
                    class="grey lighten-5 mb-2 rounded border-panel"
                >
                  <v-expansion-panel-header class="py-2">
                    Drehung / Format
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <!-- Rotation -->
                    <div class="mb-4">
                      <div class="caption grey--text mb-1">
                        Rotation ({{ (localCanvas.rotation || 0).toFixed(0) }}°)
                      </div>
                      <div class="d-flex align-center">
                        <v-btn icon small @click="rotateLeft90">
                          <v-icon small>mdi-rotate-left</v-icon>
                        </v-btn>
                        <v-btn
                            icon
                            small
                            @click="localCanvas.rotation = localCanvas.rotation - 1"
                        >
                          <v-icon small>mdi-minus</v-icon>
                        </v-btn>
                        <v-slider
                            v-model="localCanvas.rotation"
                            :min="-180"
                            :max="180"
                            step="1"
                            dense
                            hide-details
                            class="mx-2"
                        ></v-slider>
                        <v-btn
                            icon
                            small
                            @click="localCanvas.rotation = localCanvas.rotation + 1"
                        >
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn icon small @click="rotateRight90">
                          <v-icon small>mdi-rotate-right</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <!-- Canvas Format -->
                    <div class="mb-4">
                      <div class="caption grey--text mb-1">
                        Canvas-Format
                      </div>
                      <v-btn-toggle
                          v-model="localOrientation"
                          dense
                          rounded
                          mandatory
                          style="display: flex"
                          @change="onOrientationChange"
                      >
                        <v-btn style="flex: 1" value="portrait">Hochformat</v-btn>
                        <v-btn style="flex: 1" value="landscape">Querformat</v-btn>
                      </v-btn-toggle>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <!-- SEND TO FULFILL -->
            <v-divider class="mt-4 mb-3"></v-divider>

            <div class="live-action-container">
              <template v-if="!isRatingView && !isRefinementView">
                <div class="d-flex">
                  <v-btn
                      class="mt-2 mr-2"
                      color="warning"
                      @click="() => openRevisionDialog(true)"
                  >
                    <v-icon>mdi-help-box</v-icon>
                  </v-btn>

                  <div class="flex-grow-1 d-flex flex-column">
                    <v-btn
                        block
                        class="mt-2"
                        color="warning"
                        :loading="isSending"
                        :disabled="isSending || !currentImageUrl"
                        @click="openRevisionDialog(false)"
                    >
                      <v-icon left>mdi-alert</v-icon>
                      Überarbeitung Nötig
                    </v-btn>
                  </div>
                </div>
                <v-divider class="mt-2 mb-2"></v-divider>
              </template>
              <div v-if="!isRefinementView" class="d-flex">
                <v-btn
                    class="flex-grow-1"
                    color="primary"
                    :loading="isSending"
                    :disabled="isSending || !currentImageUrl"
                    @click="() => sendToFulfill(true)"
                >
                  <v-icon left>mdi-truck-check</v-icon>
                  <v-icon left>mdi-mail</v-icon>
                  Preview
                </v-btn>
                <v-btn
                    v-if="!isRatingView"
                    color="success"
                    class="ml-2 flex-grow-1"
                    :loading="isSending"
                    :disabled="isSending || !currentImageUrl"
                    @click="() => sendToFulfill(false)"
                >
                  <v-icon left>mdi-truck-check</v-icon>
                  Fulfill
                </v-btn>
              </div>
              <template v-if="isRefinementView">
                <v-btn
                    v-if="!isRatingView"
                    block
                    color="success"
                    class="mt-2"
                    :loading="isSending"
                    :disabled="isSending || !localRefinementArtwork"
                    @click="() => approveRefinement()"
                >
                  <v-icon left>mdi-check</v-icon>
                  Upload
                </v-btn>
              </template>
              <div
                  v-if="isRefinementView && !localRefinementArtwork"
                  class="mt-2 red--text text-caption"
              >
                Please Upload Refinement
              </div>
              <div v-if="sendError" class="mt-2 red--text text-caption">
                {{ sendError }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Bild-Vorschau -->
    <ImageDialog
        v-if="dialogImage"
        :show="!!dialogImage"
        :image="dialogImage"
        :onClose="() => (dialogImage = null)"
    ></ImageDialog>

    <!-- Vergleichs-Dialog -->
    <v-dialog v-model="compareDialog" max-width="1200px">
      <v-card class="compare-dialog-card">
        <v-card-title
            class="headline d-flex justify-space-between align-center"
        >
          <span>Vergleich – Artwork / Original</span>
          <div class="d-flex align-center">
            <v-btn icon small @click="resetCompareView">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon small @click="compareDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <div
              class="compare-dialog-container"
              @mousedown.prevent="compareOnMouseDown"
              @mousemove.prevent="compareOnMouseMove"
              @mouseup.prevent="compareOnMouseUp"
              @mouseleave.prevent="compareOnMouseUp"
              @wheel.prevent="compareOnWheel"
          >
            <div class="compare-pane">
              <div class="compare-pane-label">Artwork</div>
              <div class="compare-pane-inner">
                <img
                    v-if="currentImageUrl"
                    :src="currentImageUrl"
                    class="compare-dialog-img"
                    draggable="false"
                    :style="compareTransformStyle"
                />
              </div>
            </div>
            <div class="compare-pane">
              <div class="compare-pane-label">Original</div>
              <div class="compare-pane-inner">
                <img
                    v-if="customizationData.originalImage"
                    :src="customizationData.originalImage"
                    class="compare-dialog-img"
                    draggable="false"
                    :style="compareTransformStyle"
                />
              </div>
            </div>
          </div>
          <div class="caption grey--text text-center mt-2">
            Scrollrad = Zoom · Mausziehen = Verschieben
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Überarbeitung-Dialog -->
    <v-dialog v-model="showRevisionDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline"> Überarbeitung nötig </v-card-title>
        <v-card-text>
          <div class="floating-actions">
            <v-btn
                @click="
                revisionComment =
                  'Remove watercolor stains from Faces/Skin'
              "
                x-small
                outlined
                class="ma-1"
            >
              Remove stains In Face
            </v-btn>
            <v-btn
                @click="revisionComment = 'Optimize Face Details'"
                x-small
                outlined
                class="ma-1"
            >
              Optimize Face Details
            </v-btn>
            <v-btn
                @click="revisionComment = 'Remove Numberplate'"
                x-small
                outlined
                class="ma-1"
            >
              Remove Numberplate
            </v-btn>
          </div>
          <div class="text-field-wrapper">
            <v-textarea
                v-model="revisionComment"
                outlined
                label="Beschreibung"
                rows="4"
            ></v-textarea>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showRevisionDialog = false"> Abbrechen </v-btn>
          <v-btn color="primary" @click="confirmRevision"> Bestätigen </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import ImageDialog from "@/components/ImageDialog.vue";
import {
  currentMonthYear,
  downloadFileFromUrl,
  downloadImageFromUrl,
  getDateFromNow,
} from "@/utils";
import { mapActions, mapGetters } from "vuex";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../../fb";

export default {
  name: "LiveEditorDialog",
  components: { ImageDialog },
  props: {
    value: Boolean,
    task: Object,
    isRatingView: Boolean,
    isRefinementView: Boolean,
    customizationData: {
      type: Object,
      required: true,
    },
    orderName: String,
    upscaledUrls: {
      type: Object,
      default: () => ({}),
    },
    refinementArtwork: {
      type: String,
      default: null,
    },
    onApprove: {
      type: Function,
      default: null,
    },
    storageUploadFn: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      getDateFromNow,
      isAdminRevision: false,
      showRevisionDialog: false,
      dialogImage: null,

      // lokales Custom Artwork (z.B. via Drag & Drop)
      localRefinementArtwork: null,
      isFrameDragOver: false,

      // Kommentar für Überarbeitung
      revisionComment: "",

      // Artwork Canvas State
      localCanvas: { x: 0, y: 0, scale: 1, rotation: 0 },
      localTextElements: [],

      selectedVariant: "ORIGINAL",
      openPanels: [],
      openArtworkPanels: [],

      // Canvas-Ausrichtung
      localOrientation: null,

      isKeyboardZoom: false,

      // Vergleichs-Dialog
      compareDialog: false,
      compareZoom: 2,
      compareOffset: { x: 0, y: 0 },
      compareDrag: {
        active: false,
        startX: 0,
        startY: 0,
        origX: 0,
        origY: 0,
      },

      // Größen
      canvasRect: { width: 0, height: 0 },
      frameRect: { width: 0, height: 0 },
      imageAspectRatio: 1,

      // Dragging
      dragMode: null,
      dragStart: { x: 0, y: 0 },
      dragStartCanvas: { x: 0, y: 0 },
      dragTextIndex: null,
      dragStartTextPos: { x: 0, y: 0 },

      zoomProxy: 1,

      isSending: false,
      sendError: null,

      fontOptions: [
        "WindSong",
        "Playfair Display",
        "Montserrat",
        "Raleway",
        "Dancing Script",
        "Anton",
        "Cinzel",
        "Permanent Marker",
      ],
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    // Basis-Konfiguration: bevorzugt savedConfig
    config() {
      return (
          this.customizationData.savedConfig ||
          this.customizationData.configuration ||
          {}
      );
    },

    updatedCustomizationConfigData() {
      return {
        ...this.config,
        orientation: this.effectiveOrientation,
        artworkCanvas: { ...this.localCanvas },
        textElements: this.localTextElements
            ? this.localTextElements.map((el) => ({ ...el }))
            : [],
      };
    },

    effectiveRefinementArtwork() {
      return (
          this.localRefinementArtwork ||
          this.customizationData.refinementArtwork ||
          this.refinementArtwork ||
          null
      );
    },

    configMaterial() {
      return this.config.material || "Poster";
    },
    configOrientation() {
      return this.config.orientation || "portrait";
    },
    effectiveOrientation() {
      return this.localOrientation || this.configOrientation;
    },
    configSize() {
      return this.config.size || "M";
    },

    validUpscales() {
      const valid = {};
      for (const [key, val] of Object.entries(this.upscaledUrls || {})) {
        const url = typeof val === "object" && val && val.url ? val.url : val;
        if (url && typeof url === "string" && url.startsWith("http")) {
          valid[key] = url;
        }
      }
      return valid;
    },

    currentImageUrl() {
      if (this.selectedVariant === "REFINEMENT") {
        return this.effectiveRefinementArtwork;
      }
      if (this.selectedVariant === "ORIGINAL") {
        return (
            this.customizationData.artworkImageUrl ||
            this.customizationData.originalImage
        );
      }
      const val = this.upscaledUrls[this.selectedVariant];
      return typeof val === "object" && val ? val.url : val;
    },

    targetAspectRatio() {
      return this.effectiveOrientation === "landscape" ? 3 / 2 : 2 / 3;
    },

    zoomMin() {
      return 1;
    },
    zoomMax() {
      return this.effectiveOrientation === "landscape" ? 4 : 5;
    },

    frameContainerStyle() {
      const ratio = this.targetAspectRatio;
      const cw = this.canvasRect.width;
      const ch = this.canvasRect.height;

      if (!cw || !ch) {
        const h = 70;
        const w = h * ratio;
        return {
          height: `${h}vh`,
          width: `${w}vh`,
        };
      }

      const maxW = cw * 0.8;
      const maxH = ch * 0.8;

      let w = maxW;
      let h = w / ratio;

      if (h > maxH) {
        h = maxH;
        w = h * ratio;
      }

      return {
        width: `${w}px`,
        height: `${h}px`,
      };
    },

    imageLayout() {
      if (
          !this.frameRect.width ||
          !this.frameRect.height ||
          !this.imageAspectRatio
      ) {
        return null;
      }

      const frameW = this.frameRect.width;
      const frameH = this.frameRect.height;
      const frameRatio = frameW / frameH;
      const imgRatio = this.imageAspectRatio;

      let baseW, baseH;

      if (imgRatio > frameRatio) {
        baseH = frameH;
        baseW = baseH * imgRatio;
      } else {
        baseW = frameW;
        baseH = baseW / imgRatio;
      }

      const currentW = baseW * this.localCanvas.scale;
      const currentH = baseH * this.localCanvas.scale;

      const translateX = this.localCanvas.x * currentW;
      const translateY = this.localCanvas.y * currentH;

      return {
        baseW,
        baseH,
        currentW,
        currentH,
        translateX,
        translateY,
      };
    },

    artworkImageStyle() {
      const layout = this.imageLayout;

      const style = {
        position: "absolute",
        top: "0",
        left: "0",
        transformOrigin: "0 0",              // WICHTIG wie früher
        willChange: "transform",
        pointerEvents: "none",
        userSelect: "none",
      };

      if (!layout) {
        style.width = "100%";
        style.height = "100%";
        style.transform = "none";
        return style;
      }

      style.width = `${layout.baseW}px`;
      style.height = `${layout.baseH}px`;

      const rotation = this.localCanvas.rotation || 0;

      style.transform =
          `translate(${layout.translateX}px, ${layout.translateY}px)` +
          (rotation ? ` rotate(${rotation}deg)` : "") +
          ` scale(${this.localCanvas.scale})`;

      return style;
    },

    compareTransformStyle() {
      return {
        transformOrigin: "50% 50%",
        transform: `translate(${this.compareOffset.x}px, ${this.compareOffset.y}px) scale(${this.compareZoom})`,
      };
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.initData();

        this.$nextTick(() => {
          window.requestAnimationFrame(() => {
            this.updateMeasurements();
            this.$nextTick(() => this.clampCanvas());
          });
        });
      }
    },
  },
  created() {

  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    window.addEventListener("mouseup", this.stopDrag);
    window.addEventListener("mousemove", this.onDrag);

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("mouseup", this.stopDrag);
    window.removeEventListener("mousemove", this.onDrag);

    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);

    if (
        this.localRefinementArtwork &&
        typeof this.localRefinementArtwork === "string" &&
        this.localRefinementArtwork.startsWith("blob:")
    ) {
      try {
        URL.revokeObjectURL(this.localRefinementArtwork);
      } catch (e) {}
    }
  },
  methods: {
    downloadFileFromUrl,
    ...mapActions(["markTask", "importLiveTaskRefinement"]),
    onOrientationChange() {
      // Nur wenn der User im Editor das Format wechselt,
      // NICHT beim initialen Laden aus der Config.
      this.localCanvas = {
        ...this.localCanvas,
        x: 0,
        y: 0,
        scale: 1,
      };
      this.zoomProxy = 1;

      this.$nextTick(() => {
        this.updateMeasurements();
        this.$nextTick(() => this.clampCanvas());
      });
    },

    onKeyDown(e) {
      if (!this.show) return;
      if (e.key !== "y" && e.key !== "Y") return;

      const tag = e.target.tagName;
      const isInput =
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          e.target.isContentEditable;

      if (isInput) return;
      if (this.dialogImage) return;
      if (this.compareDialog) return (this.compareDialog = false);

      if (this.currentImageUrl) {
        this.isKeyboardZoom = true;
        this.openCompareDialog();
      }
    },

    onKeyUp(e) {
      if (!this.show) return;
      if (e.key !== "y" && e.key !== "Y") return;

      const tag = e.target.tagName;
      const isInput =
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          e.target.isContentEditable;

      if (isInput) return;

      this.isKeyboardZoom = false;
    },

    openRevisionDialog(isAdminRevision) {
      this.isAdminRevision = isAdminRevision;
      this.revisionComment = "";
      this.showRevisionDialog = true;
    },

    initData() {
      const config = this.config || {};
      const canvasCfg = config.artworkCanvas || {};

      let rotation = 0;
      if (typeof canvasCfg.rotation === "number") {
        let r = canvasCfg.rotation;
        r = r % 360;
        if (r > 180) r -= 360;
        if (r < -180) r += 360;
        rotation = r;
      }

      this.localCanvas = {
        x: typeof canvasCfg.x === "number" ? canvasCfg.x : 0,
        y: typeof canvasCfg.y === "number" ? canvasCfg.y : 0,
        scale: typeof canvasCfg.scale === "number" ? canvasCfg.scale : 1,
        rotation,
      };

      this.zoomProxy = this.localCanvas.scale || 1;
      this.localOrientation = config.orientation || this.configOrientation;

      this.localTextElements = config.textElements
          ? JSON.parse(JSON.stringify(config.textElements))
          : [];

      this.localRefinementArtwork = null;

      if (this.effectiveRefinementArtwork) {
        this.selectedVariant = "REFINEMENT";
      } else if (this.upscaledUrls["DEFAULT"]) {
        this.selectedVariant = "DEFAULT";
      } else {
        this.selectedVariant = "ORIGINAL";
      }

      this.openPanels = [];
      this.openArtworkPanels = [];

      this.resetCompareView();
    },

    updateMeasurements() {
      this.measureCanvas();
      this.$nextTick(() => {
        this.measureFrame();
      });
    },

    async downloadArtwork() {
      await downloadImageFromUrl(
          this.currentImageUrl,
          `${this.task.orderName}_${this.mapVariantName(this.selectedVariant)}`,
          () => {},
          "jpg"
      );
    },

    measureCanvas() {
      if (this.$refs.canvasRef) {
        const rect = this.$refs.canvasRef.getBoundingClientRect();
        this.canvasRect = { width: rect.width, height: rect.height };
      }
    },

    measureFrame() {
      if (this.$refs.frameRef) {
        const rect = this.$refs.frameRef.getBoundingClientRect();
        this.frameRect = { width: rect.width, height: rect.height };
      }
    },

    onResize() {
      this.updateMeasurements();
    },

    onImageLoad(e) {
      const { naturalWidth, naturalHeight } = e.target;
      if (naturalHeight > 0) {
        this.imageAspectRatio = naturalWidth / naturalHeight;
      }

      if (!this.show) return; // falls v-show den Dialog gerade versteckt hält

      this.$nextTick(() => {
        this.updateMeasurements();
        this.$nextTick(() => this.clampCanvas());
      });
    },


    mapVariantName(key) {
      const map = { DEFAULT: "Upscale", PRO_CAR: "Car", PRO_PERSON: "Person" };
      return map[key] || key;
    },

    getTextStyle(text) {
      const frameW = this.frameRect.width || 500;
      const sizePx = (frameW * text.size) / 100;

      return {
        position: "absolute",
        left: `${text.x}%`,
        top: `${text.y}%`,
        transform: "translate(-50%, -50%)",
        fontFamily: text.font,
        fontSize: `${sizePx}px`,
        color: text.color,
        lineHeight: 1.1,
        whiteSpace: "nowrap",
        cursor: "move",
        pointerEvents: "auto",
        userSelect: "none",
      };
    },

    resetTexts() {
      const cfg = this.config || {};
      this.localTextElements = JSON.parse(
          JSON.stringify(cfg.textElements || [])
      );
    },

    deleteTexts() {
      this.localTextElements = this.localTextElements.map((tEl) => {
        return { ...tEl, text: "" };
      });
    },

    openColorPicker(index) {
      const ref = this.$refs["cp" + index];
      if (ref && typeof ref.click === "function") {
        ref.click();
      }
    },

    // --- DRAG ARTWORK ---
    startArtworkDrag(e) {
      if (!this.imageLayout) return;
      if (this.dragMode === "text") return;
      this.dragMode = "artwork";
      this.dragStart = { x: e.clientX, y: e.clientY };
      this.dragStartCanvas = { ...this.localCanvas };
    },

    dragArtwork(e) {
      if (this.dragMode !== "artwork" || !this.imageLayout) return;

      const layout = this.imageLayout;
      const dxPx = e.clientX - this.dragStart.x;
      const dyPx = e.clientY - this.dragStart.y;

      const dXPct = dxPx / layout.currentW;
      const dYPct = dyPx / layout.currentH;

      this.localCanvas.x = this.dragStartCanvas.x + dXPct;
      this.localCanvas.y = this.dragStartCanvas.y + dYPct;

      this.clampCanvas();
    },

    // --- DRAG TEXT ---
    startTextDrag(e, index) {
      if (!this.frameRect.width || !this.localTextElements[index]) return;
      this.dragMode = "text";
      this.dragTextIndex = index;
      this.dragStart = { x: e.clientX, y: e.clientY };
      this.dragStartTextPos = {
        x: this.localTextElements[index].x,
        y: this.localTextElements[index].y,
      };
    },

    dragText(e) {
      if (this.dragMode !== "text") return;
      const idx = this.dragTextIndex;
      if (idx == null || !this.localTextElements[idx]) return;

      const dxPx = e.clientX - this.dragStart.x;
      const dyPx = e.clientY - this.dragStart.y;

      const frameW = this.frameRect.width || 1;
      const frameH = this.frameRect.height || 1;

      const dXPct = (dxPx / frameW) * 100;
      const dYPct = (dyPx / frameH) * 100;

      const el = this.localTextElements[idx];
      let newX = this.dragStartTextPos.x + dXPct;
      let newY = this.dragStartTextPos.y + dYPct;

      const SNAP_THRESHOLD = 2;
      if (Math.abs(newX - 50) < SNAP_THRESHOLD) {
        newX = 50;
      }
      if (Math.abs(newY - 50) < SNAP_THRESHOLD) {
        newY = 50;
      }

      el.x = newX;
      el.y = newY;
    },

    onDrag(e) {
      if (!this.dragMode) return;
      if (this.dragMode === "artwork") {
        this.dragArtwork(e);
      } else if (this.dragMode === "text") {
        this.dragText(e);
      }
    },

    stopDrag() {
      this.dragMode = null;
      this.dragTextIndex = null;
    },

    // --- DRAG & DROP CUSTOM IMAGE ---
    onFrameDragOver(e) {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
    },
    onFrameDragEnter() {
      this.isFrameDragOver = true;
    },
    onFrameDragLeave(e) {
      if (
          e.currentTarget &&
          e.relatedTarget &&
          e.currentTarget.contains(e.relatedTarget)
      )
        return;
      this.isFrameDragOver = false;
    },
    onFrameDrop(e) {
      this.isFrameDragOver = false;
      const dt = e.dataTransfer;
      if (!dt) return;

      if (dt.files && dt.files.length) {
        const file = Array.from(dt.files).find(
            (f) => f.type && f.type.startsWith("image/")
        );
        if (file) {
          const objectUrl = URL.createObjectURL(file);
          this.applyCustomImage(objectUrl);
          return;
        }
      }

      if (dt.items && dt.items.length) {
        for (let i = 0; i < dt.items.length; i++) {
          const item = dt.items[i];
          if (item.kind === "string" && item.type === "text/uri-list") {
            item.getAsString((url) => {
              if (url) {
                this.applyCustomImage(url.trim());
              }
            });
            return;
          }
        }
      }
    },
    applyCustomImage(url) {
      if (
          this.localRefinementArtwork &&
          typeof this.localRefinementArtwork === "string" &&
          this.localRefinementArtwork.startsWith("blob:")
      ) {
        try {
          URL.revokeObjectURL(this.localRefinementArtwork);
        } catch (e) {}
      }
      this.localRefinementArtwork = url;
      this.selectedVariant = "REFINEMENT";
    },

    clampCanvas() {
      const layout = this.imageLayout;
      if (!layout || !this.frameRect.width || !this.frameRect.height) return;

      const frameW = this.frameRect.width;
      const frameH = this.frameRect.height;

      const currentW = layout.currentW;
      const currentH = layout.currentH;

      const minX = frameW - currentW;
      const maxX = 0;
      const minY = frameH - currentH;
      const maxY = 0;

      const minXPct = minX / currentW;
      const maxXPct = maxX / currentW;
      const minYPct = minY / currentH;
      const maxYPct = maxY / currentH;

      let x = this.localCanvas.x;
      let y = this.localCanvas.y;

      if (x < minXPct) x = minXPct;
      if (x > maxXPct) x = maxXPct;
      if (y < minYPct) y = minYPct;
      if (y > maxYPct) y = maxYPct;

      this.localCanvas.x = x;
      this.localCanvas.y = y;
    },

    // --- ZOOM ---
    handleZoom(newScale, oldScale) {
      if (
          !this.frameRect.width ||
          !this.frameRect.height ||
          !this.imageAspectRatio
      ) return;
      if (newScale === oldScale) return;

      const frameW = this.frameRect.width;
      const frameH = this.frameRect.height;
      const frameRatio = frameW / frameH;
      const imgRatio = this.imageAspectRatio;

      let baseW, baseH;
      if (imgRatio > frameRatio) {
        baseH = frameH;
        baseW = baseH * imgRatio;
      } else {
        baseW = frameW;
        baseH = baseW / imgRatio;
      }

      const currentW = baseW * oldScale;
      const currentH = baseH * oldScale;

      const ratio = newScale / oldScale;

      const currentPixelX = this.localCanvas.x * currentW;
      const currentPixelY = this.localCanvas.y * currentH;

      const newPixelX = currentPixelX * ratio + (frameW / 2) * (1 - ratio);
      const newPixelY = currentPixelY * ratio + (frameH / 2) * (1 - ratio);

      const newW = currentW * ratio;
      const newH = currentH * ratio;

      const newX = newPixelX / newW;
      const newY = newPixelY / newH;

      this.localCanvas = {
        ...this.localCanvas,
        x: newX,
        y: newY,
        scale: newScale,
      };

      this.$nextTick(() => {
        this.clampCanvas();
        this.zoomProxy = this.localCanvas.scale;
      });
    },

    onSliderZoom(value) {
      const v = parseFloat(value);
      const newScale = Math.min(Math.max(v, this.zoomMin), this.zoomMax);
      const oldScale = this.localCanvas.scale || 1;
      this.handleZoom(newScale, oldScale);
    },

    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      let next = (this.localCanvas.scale || 1) + delta;
      next = Math.min(Math.max(next, this.zoomMin), this.zoomMax);
      this.handleZoom(next, this.localCanvas.scale || 1);
    },

    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();

        if (typeof url === "string" && url.startsWith("http")) {
          img.crossOrigin = "anonymous";
        }

        img.onload = () => resolve(img);
        img.onerror = (e) => {
          console.error("Image load error", e);
          reject(e);
        };
        img.src = url;
      });
    },

    canvasToBlob(canvas, type = "image/jpeg", quality = 0.9) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error("Failed to create blob from canvas"));
            },
            type,
            quality
        );
      });
    },

    generateShortId() {
      return Math.random().toString(36).substr(2, 6);
    },

    async uploadToStorage(blob, path) {
      const storage = getStorage();
      const storageRef = ref(storage, path);
      const uploadResponse = await uploadBytes(storageRef, blob);
      return await getDownloadURL(uploadResponse.ref);
    },

    // *** WICHTIG: Render-Logik = Editor-Logik ***
    renderArtworkToCanvas(ctx, img, frameW, frameH) {
      const imgW = img.naturalWidth;
      const imgH = img.naturalHeight;
      if (!imgW || !imgH) return;

      const imgRatio = imgW / imgH;
      const frameRatio = frameW / frameH;

      let baseScale;
      if (imgRatio > frameRatio) {
        baseScale = frameH / imgH;   // Höhe passend
      } else {
        baseScale = frameW / imgW;   // Breite passend
      }

      const scale = this.localCanvas.scale || 1;
      const finalScale = baseScale * scale;

      const scaledW = imgW * finalScale;
      const scaledH = imgH * finalScale;

      const offsetX = this.localCanvas.x * scaledW;
      const offsetY = this.localCanvas.y * scaledH;

      const angleDeg = this.localCanvas.rotation || 0;
      const angleRad = (angleDeg * Math.PI) / 180;

      ctx.save();

      // entspricht CSS: translate(offset) rotate(angle) scale(finalScale)
      ctx.translate(offsetX, offsetY);
      if (angleRad !== 0) {
        ctx.rotate(angleRad);
      }
      ctx.scale(finalScale, finalScale);
      ctx.drawImage(img, 0, 0);

      ctx.restore();
    },

    renderTextsToCanvas(ctx, frameW, frameH) {
      if (!Array.isArray(this.localTextElements)) return;

      this.localTextElements.forEach((el) => {
        if (!el || !el.text) return;

        const fontSizePx = (frameW * el.size) / 100;
        const x = (el.x / 100) * frameW;
        const y = (el.y / 100) * frameH;

        ctx.save();
        ctx.font = `${fontSizePx}px ${el.font || "sans-serif"}`;
        ctx.fillStyle = el.color || "#000000";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(el.text, x, y);
        ctx.restore();
      });
    },

    resetArtworkTransform() {
      this.localCanvas.x = 0;
      this.localCanvas.y = 0;
      this.localCanvas.scale = 1;
      this.localCanvas.rotation = 0;
      this.zoomProxy = 1;
      this.$nextTick(() => this.clampCanvas());
    },

    rotateRight90() {
      let angle = this.localCanvas.rotation || 0;
      const step = 90;
      let next = Math.ceil(angle / step) * step;
      if (next === angle) {
        next += step;
      }
      if (next > 180) {
        next = -180;
      }
      this.localCanvas.rotation = next;
    },

    rotateLeft90() {
      let angle = this.localCanvas.rotation || 0;
      const step = 90;
      let prev = Math.floor(angle / step) * step;
      if (prev === angle) {
        prev -= step;
      }
      if (prev < -180) {
        prev = 180;
      }
      this.localCanvas.rotation = prev;
    },

    confirmRevision() {
      if (this.isAdminRevision) {
        this.markTask({
          uid: this.task.uid,
          revisionComment: this.revisionComment,
        });
      } else {
        this.showRevisionDialog = false;
        this.$emit("refinement", {
          revisionComment: this.revisionComment,
          customOriginalImage: null,
          customFinalImage:
              this.localRefinementArtwork || this.refinementArtwork || null,
          currentConfig: this.updatedCustomizationConfigData,
        });
        this.$emit("approveProcessed");
      }
      this.isAdminRevision = false;
    },

    async urlToBlob(url) {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error("Konnte Refinement-Bild nicht laden.");
      }
      return await res.blob();
    },

    async approveRefinement() {
      try {
        this.isSending = true;
        const fileName = this.getSafeFileName(this.task.orderName);
        const blob = await this.urlToBlob(this.localRefinementArtwork);
        const url = await this.uploadToStorage(
            blob,
            `templates/${currentMonthYear}/refinement/${fileName}`
        );

        await setDoc(
            doc(db, `tasks`, this.task?.uid.toString()),
            {
              status: "REVIEW",
              reviewType: "REFINEMENT",
              editors: {
                REFINEMENT: this.currentUser.uid,
              },
              customizationData: {
                refinementArtwork: url,
                savedConfig: this.updatedCustomizationConfigData,
              },
            },
            { merge: true }
        );
      } catch (e) {
        console.error(e);
        this.sendError = e && e.message ? e.message : String(e);
      } finally {
        this.isSending = false;
      }
    },

    getSafeFileName() {
      const shortId = this.generateShortId();
      const baseName = (this.orderName || "order").toString().trim();
      const safeOrderName =
          baseName
              .replace(/\s+/g, "-")
              .replace(/[^a-zA-Z0-9-_]/g, "")
              .toLowerCase() || "order";
      return `${safeOrderName}-${shortId}.jpg`;
    },

    // SEND TO FULFILL
    async sendToFulfill(sendPreviewCustomer = false) {
      try {
        this.isSending = true;
        this.sendError = null;
        this.$emit("approveProcessed");

        function extractSize(str) {
          if (!str || typeof str !== "string") return null;
          const lastPart = str.split("x")?.[0]?.trim();
          const match = lastPart.match(/^(\d+)/);
          return match ? Number(match[1]) : null;
        }

        const size = extractSize(this.config.size);
        const maxSize = size && size > 45 ? 8000 : 4000;

        const orientation = this.effectiveOrientation || "portrait";
        const ratio = orientation === "landscape" ? 3 / 2 : 2 / 3;

        let width, height;
        if (orientation === "landscape") {
          width = maxSize;
          height = Math.round(width / ratio);
        } else {
          height = maxSize;
          width = Math.round(height * ratio);
        }

        const imgUrl =
            this.currentImageUrl ||
            this.customizationData.artworkImageUrl ||
            this.customizationData.originalImage;

        if (!imgUrl) {
          throw new Error("No artwork image URL available.");
        }

        const img = await this.loadImage(imgUrl);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);

        this.renderArtworkToCanvas(ctx, img, width, height);
        this.renderTextsToCanvas(ctx, width, height);

        const blob = await this.canvasToBlob(canvas, "image/jpeg", 0.9);

        const fileName = this.getSafeFileName(this.task.orderName);
        const path = `templates/${currentMonthYear}/final/${fileName}`;
        const url = await this.uploadToStorage(blob, path);

        let urlRefinement = null;
        if (this.localRefinementArtwork) {
          const blobRefinement = await this.urlToBlob(
              this.localRefinementArtwork
          );
          urlRefinement = await this.uploadToStorage(
              blobRefinement,
              `templates/${currentMonthYear}/refinement/${fileName}`
          );
        }

        this.$emit("approve", {
          finalUrl: url,
          refinementUrl: urlRefinement,
          currentConfig: this.updatedCustomizationConfigData,
          sendPreviewCustomer: sendPreviewCustomer,
        });

        this.$emit("approved", url);
      } catch (err) {
        console.error(err);
        this.sendError = err && err.message ? err.message : String(err);
      } finally {
        this.isSending = false;
      }
    },

    // --- Vergleichs-Dialog ---
    openCompareDialog() {
      this.resetCompareView();
      this.compareDialog = true;
    },
    resetCompareView() {
      this.compareZoom = 2;
      this.compareOffset = { x: 0, y: 0 };
      this.compareDrag = {
        active: false,
        startX: 0,
        startY: 0,
        origX: 0,
        origY: 0,
      };
    },
    compareOnWheel(e) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      let next = this.compareZoom + delta;
      next = Math.min(Math.max(next, 0.5), 10);
      this.compareZoom = next;
    },
    compareOnMouseDown(e) {
      this.compareDrag.active = true;
      this.compareDrag.startX = e.clientX;
      this.compareDrag.startY = e.clientY;
      this.compareDrag.origX = this.compareOffset.x;
      this.compareDrag.origY = this.compareOffset.y;
    },
    compareOnMouseMove(e) {
      if (!this.compareDrag.active) return;
      const dx = e.clientX - this.compareDrag.startX;
      const dy = e.clientY - this.compareDrag.startY;
      this.compareOffset = {
        x: this.compareDrag.origX + dx,
        y: this.compareDrag.origY + dy,
      };
    },
    compareOnMouseUp() {
      this.compareDrag.active = false;
    },

    // --- SAVE / CLOSE ---
    save() {
      this.$emit("save", {
        id: this.customizationData.id,
        configuration: {
          ...this.config,
          artworkCanvas: { ...this.localCanvas },
          textElements: this.localTextElements,
          orientation: this.effectiveOrientation,
        },
        selectedUpscaleVariant: this.selectedVariant,
      });
      this.close();
    },
    close() {
      this.stopDrag();
      this.showRevisionDialog = false;
      this.isFrameDragOver = false;
      this.compareDialog = false;
      this.show = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.text-field-wrapper {
  position: relative;
}

.floating-actions {
  display: flex;
  gap: 6px;
  z-index: 10;
  background: transparent;
}

.live-action-container {
  margin-top: auto;
  margin-bottom: 12px;
  text-align: center;
  padding-top: 8px;
  border-top: 1px solid #eee;
  background: white;
}

.live-editor-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f5f5;
  overflow: hidden;
}

.editor-body {
  display: flex;
  flex: 1;
  height: calc(100% - 48px);
  overflow: hidden;
}

/* LEFT SIDE */
.canvas-container {
  flex: 1;
  background-color: #333;
  position: relative;
  display: flex;
  justify-content: center;
  overflow: hidden;
  flex-direction: column;
  align-items: stretch;
}

.canvas-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.zoom-hint {
  background-color: #ffffff;
  position: absolute;
  top: 65px;
  right: 15px;
  border-radius: 30px;
  padding: 9px;
  display: flex;
  z-index: 10;
  cursor: pointer;
}

.print-frame {
  background-color: white;
  position: relative;
  overflow: hidden;
  cursor: grab;
  flex: 0 0 auto;
}

.print-frame:active {
  cursor: grabbing;
}

.print-frame--drag-over {
  outline: 2px dashed rgba(25, 118, 210, 0.9);
  outline-offset: -4px;
}

.v-btn-toggle {
  button {
    text-transform: none !important;
  }
}

.artwork-layer {
  display: block;
}

/* Grid overlay */
.grid-lines {
  display: none;
  position: absolute;
  inset: 0;
  pointer-events: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background-image: linear-gradient(
          to right,
          rgba(0, 0, 0, 1) 1px,
          transparent 1px
  ),
  linear-gradient(to bottom, rgba(0, 0, 0, 1) 1px, transparent 1px);
  background-size: 33.3% 33.3%;
  z-index: 10;
}

/* Floating controls */
.floating-controls {
  margin-top: 16px;
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 450px;
  z-index: 100;
}

/* RIGHT SIDE */
.settings-sidebar {
  width: 360px;
  background: white;
  border-left: 1px solid #ddd;
  z-index: 20;

  display: flex;
  flex-direction: column;
}

.sidebar-inner {
  min-height: 100%;
  position: relative;

  display: flex;
  flex-direction: column;
  flex: 1;
}

.sidebar-inner-settings {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 16px;
}

.preview-box {
  position: relative;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.preview-box--download-btn {
  position: absolute;
  top: 5px;
  right: 5px;
}

.preview-box--warning {
  background-color: #ffd88d;
}

.preview-img {
  max-width: 100%;
  object-fit: contain;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.preview-img-main {
  max-height: 260px;
}

.preview-box-small {
  background-color: #fafafa;
}

.preview-img-small {
  max-height: 90px;
  box-shadow: none;
}

/* Color picker */
.color-swatch {
  width: 100%;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #ccc;
  cursor: pointer;
  position: relative;
}

.hidden-input {
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.border-panel {
  border: 1px solid #eee !important;
}

/* Text layer */
.text-layer {
  z-index: 20;
}

/* Vergleichs-Dialog */
.compare-dialog-card {
  max-height: 90vh;
}

.compare-dialog-container {
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 70vh;
  cursor: grab;
}

.compare-dialog-container:active {
  cursor: grabbing;
}

.compare-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
}

.compare-pane-label {
  padding: 4px 8px;
  font-size: 12px;
  color: #fff;
  background: rgba(0, 0, 0, 0.6);
}

.compare-pane-inner {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.compare-dialog-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}
</style>
